import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';

import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import pkg from '../../../package.json';

const version = pkg.version;
const catalogos = ['empleados', 'productos', 'metodos', 'sucursales', 'proveedores', 'eventos'];

const FILAS_CATALOGOS = [
    { key: 'empleados',   label: 'Empleados',   icon: 'fa-users' },
    { key: 'productos',   label: 'Productos',   icon: 'fa-box-open' },
    { key: 'metodos',     label: 'Métodos de pago', icon: 'fa-credit-card' },
    { key: 'sucursales',  label: 'Sucursales',  icon: 'fa-store' },
    { key: 'proveedores', label: 'Proveedores', icon: 'fa-truck' },
    { key: 'eventos',     label: 'Eventos',     icon: 'fa-calendar-alt' },
];

const FILAS_OPERACIONALES = [
    { key: 'ventas',      label: 'Ventas pendientes',      icon: 'fa-receipt' },
    { key: 'vueltas',     label: 'Ventas ambulantes pendientes', icon: 'fa-rotate-left' },
    { key: 'gastos',      label: 'Gastos pendientes',      icon: 'fa-file-invoice-dollar' },
    { key: 'cortes',      label: 'Cortes pendientes',      icon: 'fa-cash-register' },
    { key: 'revisiones',  label: 'Revisiones pendientes',  icon: 'fa-tasks' },
    { key: 'asistencias', label: 'Asistencias pendientes', icon: 'fa-clock' }
];

const Configuracion = () => {
    const dispatch = useDispatch();

    const [conteos, setConteos] = useState({
        empleados: 0, productos: 0, metodos: 0,
        sucursales: 0, proveedores: 0, eventos: 0,
        ventas: 0, vueltas: 0, gastos: 0, cortes: 0, revisiones: 0, asistencias: 0, conos: 0,
    });
    const [statusSincronizar, setStatusSincronizar] = useState(false);
    const [statusParcial,    setStatusParcial]    = useState(false);
    const [bloqueadoPorCorte, setBloqueadoPorCorte] = useState(false);

    const cargarConteos = async () => {
        const sesion = await db.sesion.toArray();
        const sucursalId = sesion[0]?.sucursal?._id || sesion[0]?.sucursal;
        const bySuc = x => String(x.sucursal?._id || x.sucursal) === String(sucursalId);

        const counts = {
            // Catálogos: globales, sin filtro
            empleados:   await db.empleados.count(),
            productos:   await db.productos.count(),
            metodos:     await db.metodos.count(),
            sucursales:  await db.sucursales.count(),
            proveedores: await db.proveedores.count(),
            eventos:     await db.eventos.count(),
            // Operacionales: solo de la sucursal activa
            ventas:      await db.ventas.filter(bySuc).count(),
            vueltas:     await db.vueltas.filter(v => bySuc(v) && !v.cancelada).count(),
            gastos:      await db.gastos.filter(bySuc).count(),
            cortes:      await db.cortes.filter(bySuc).count(),
            revisiones:  await db.revisiones.filter(bySuc).count(),
            asistencias: await db.asistencias.filter(bySuc).count(),
            conos:       await db.conos.filter(bySuc).count(),
        };
        setConteos(counts);

        const corteSinImprimir = await db.cortes
            .filter(c => bySuc(c) && c.ticket_impreso === false)
            .count();
        setBloqueadoPorCorte(corteSinImprimir > 0);
    };

    useEffect(() => { cargarConteos(); }, []);

    const handleSincronizarParcial = async () => {
        setStatusParcial(true);
        try {
            const ventas            = await db.ventas.toArray();
            const ventas_productos  = await db.ventas_productos.toArray();
            const vueltas           = (await db.vueltas.toArray()).filter(v => !v.cancelada);
            const vueltaIds         = new Set(vueltas.map(v => v.id));
            const vueltas_productos = (await db.vueltas_productos.toArray()).filter(vp => vueltaIds.has(vp.vuelta_local_id));
            const gastos            = await db.gastos.toArray();

            const res = await axios.post(`${process.env.REACT_APP_API_ROUTE}/sincronizacion/subir-parcial`, {
                ventas,
                ventas_productos,
                vueltas,
                vueltas_productos,
                gastos,
            });

            // Actualizar _id locales con los devueltos por el backend
            if (res.data?.ventas) {
                await Promise.all(res.data.ventas.map(({ id, _id }) =>
                    db.ventas.update(id, { _id })
                ));
            }
            if (res.data?.vueltas) {
                await Promise.all(res.data.vueltas.map(({ id, _id }) =>
                    db.vueltas.update(id, { _id })
                ));
            }
            if (res.data?.gastos) {
                await Promise.all(res.data.gastos.map(({ id, _id }) =>
                    db.gastos.update(id, { _id })
                ));
            }

            // Descargar catálogos frescos
            const cat = await axios.get(`${process.env.REACT_APP_API_ROUTE}/sincronizacion/descargar`);
            await Promise.all(
                catalogos.map(async col => {
                    if (cat.data[col]) {
                        await db[col].clear();
                        await db[col].bulkAdd(cat.data[col]);
                    }
                })
            );

            await cargarConteos();
            dispatch(setAlert('Sincronización parcial completada correctamente', 'success'));
        } catch {
            dispatch(setAlert('Error al sincronizar. Verifique la conexión.', 'danger'));
        } finally {
            setStatusParcial(false);
        }
    };

    const handleSincronizar = async () => {
        setStatusSincronizar(true);

        try {
            // Paso 1 — Subir operacionales
            const ventas            = await db.ventas.toArray();
            const ventas_productos  = await db.ventas_productos.toArray();
            const vueltas           = (await db.vueltas.toArray()).filter(v => !v.cancelada);
            const vueltaIds         = new Set(vueltas.map(v => v.id));
            const vueltas_productos = (await db.vueltas_productos.toArray()).filter(vp => vueltaIds.has(vp.vuelta_local_id));
            const pagos_vueltas     = await db.pagos_vueltas.toArray();
            const gastos            = await db.gastos.toArray();
            const cortes            = await db.cortes.toArray();
            const revisiones        = await db.revisiones.toArray();
            const asistencias       = await db.asistencias.toArray();
            const conos             = await db.conos.toArray();

            await axios.post(`${process.env.REACT_APP_API_ROUTE}/sincronizacion/actualizar`, {
                ventas,
                ventas_productos,
                vueltas,
                vueltas_productos,
                pagos_vueltas,
                gastos,
                cortes,
                revisiones,
                asistencias,
                conos,
            });

            // Paso 2 — Limpiar operacionales subidas
            await db.ventas.clear();
            await db.ventas_productos.clear();
            await db.vueltas.clear();
            await db.vueltas_productos.clear();
            await db.pagos_vueltas.clear();
            await db.gastos.clear();
            await db.cortes.clear();
            await db.revisiones.clear();
            await db.asistencias.clear();
            await db.conos.clear();

            // Paso 3 — Bajar catálogos frescos
            const res = await axios.get(`${process.env.REACT_APP_API_ROUTE}/sincronizacion/descargar`);
            await Promise.all(
                catalogos.map(async col => {
                    if (res.data[col]) {
                        await db[col].clear();
                        await db[col].bulkAdd(res.data[col]);
                    }
                })
            );

            // Paso 4 — Actualizar conteos y notificar
            await cargarConteos();
            dispatch(setAlert('Sincronización completada correctamente', 'success'));
        } catch {
            dispatch(setAlert('Error al sincronizar. Verifique la conexión.', 'danger'));
        } finally {
            setStatusSincronizar(false);
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <i className="fas fa-cog me-3" style={{ color: '#F5C800', fontSize: '1.3rem' }}></i>
                <h4 style={styles.titulo}>Configuración</h4>
                <span style={styles.versionTag}>v{version}</span>
            </div>

            <div style={styles.grid}>
                {/* Catálogos */}
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-database me-2" style={{ color: '#F5C800' }}></i>
                        Catálogos locales
                    </div>
                    <div>
                        {FILAS_CATALOGOS.map(({ key, label, icon }) => (
                            <div key={key} style={styles.fila}>
                                <span style={styles.filaLabel}>
                                    <i className={`fas ${icon} me-2`} style={styles.filaIcon}></i>
                                    {label}
                                </span>
                                <span style={conteos[key] > 0 ? styles.badgeOk : styles.badgeVacio}>
                                    {conteos[key]} {conteos[key] === 1 ? 'registro' : 'registros'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Operacionales */}
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-upload me-2" style={{ color: '#F5C800' }}></i>
                        Pendientes de subir
                    </div>
                    <div>
                        {FILAS_OPERACIONALES.map(({ key, label, icon }) => (
                            <div key={key} style={styles.fila}>
                                <span style={styles.filaLabel}>
                                    <i className={`fas ${icon} me-2`} style={styles.filaIcon}></i>
                                    {label}
                                </span>
                                <span style={conteos[key] > 0 ? styles.badgePendiente : styles.badgeVacio}>
                                    {conteos[key]} {conteos[key] === 1 ? 'registro' : 'registros'}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <button
                            style={statusParcial ? { ...styles.btnParcial, opacity: 0.6, cursor: 'not-allowed' } : styles.btnParcial}
                            onClick={handleSincronizarParcial}
                            disabled={statusParcial || statusSincronizar}
                        >
                            {statusParcial ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    Sincronizando...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-cloud-upload-alt me-2"></i>
                                    Sincronización parcial
                                </>
                            )}
                        </button>
                        <p style={styles.hint}>
                            Sube ventas, ventas ambulantes y gastos al servidor sin borrarlos localmente.
                        </p>

                        <div style={styles.divider} />

                        <button
                            style={statusSincronizar || bloqueadoPorCorte
                                ? { ...styles.btnSync, opacity: 0.5, cursor: 'not-allowed' }
                                : styles.btnSync}
                            onClick={handleSincronizar}
                            disabled={statusSincronizar || statusParcial || bloqueadoPorCorte}
                        >
                            {statusSincronizar ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    Sincronizando...
                                </>
                            ) : (
                                <>
                                    <i className={`fas ${bloqueadoPorCorte ? 'fa-lock' : 'fa-sync-alt'} me-2`}></i>
                                    Sincronización completa
                                </>
                            )}
                        </button>
                        {bloqueadoPorCorte ? (
                            <p style={{ ...styles.hint, color: '#ED8936' }}>
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                Imprime el ticket del corte de caja antes de sincronizar.
                            </p>
                        ) : (
                            <p style={styles.hint}>
                                Sube todos los registros pendientes, los borra localmente y descarga catálogos actualizados.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    page: {
        maxWidth: '900px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
    },
    versionTag: {
        marginLeft: 'auto',
        color: '#444',
        fontSize: '0.8rem',
        fontWeight: 600,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '1.25rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '1.5rem',
    },
    cardHeader: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1rem',
        marginBottom: '1.25rem',
        paddingBottom: '0.75rem',
        borderBottom: '1px solid #2E2E2E',
    },
    fila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.65rem 0',
        borderBottom: '1px solid #222222',
    },
    filaLabel: {
        color: '#C0C0C0',
        fontSize: '0.95rem',
    },
    filaIcon: {
        color: '#555555',
        width: '16px',
    },
    badgeOk: {
        background: 'rgba(245,200,0,0.1)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.75rem',
        fontSize: '0.8rem',
        fontWeight: 600,
    },
    badgePendiente: {
        background: 'rgba(229,62,62,0.1)',
        color: '#E53E3E',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.75rem',
        fontSize: '0.8rem',
        fontWeight: 600,
    },
    badgeVacio: {
        background: '#222222',
        color: '#555555',
        border: '1px solid #2E2E2E',
        borderRadius: '20px',
        padding: '0.2rem 0.75rem',
        fontSize: '0.8rem',
        fontWeight: 600,
    },
    btnParcial: {
        background: 'transparent',
        color: '#F5C800',
        border: '2px solid #F5C800',
        borderRadius: '10px',
        padding: '0.8rem 1.5rem',
        fontWeight: 700,
        fontSize: '1rem',
        width: '100%',
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
    btnSync: {
        background: '#F5C800',
        color: '#111111',
        border: 'none',
        borderRadius: '10px',
        padding: '0.8rem 1.5rem',
        fontWeight: 800,
        fontSize: '1rem',
        width: '100%',
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
    divider: {
        borderTop: '1px solid #2E2E2E',
        margin: '0.25rem 0',
    },
    hint: {
        color: '#555555',
        fontSize: '0.8rem',
        marginTop: '0.75rem',
        marginBottom: 0,
        textAlign: 'center',
    },
};

export default Configuracion;
